// ============================================================
// INTEL HUB — Tactical intelligence interface (route: /intel)
// Aesthetic: functional brutalism + neo-noir, MCRN/Altered Carbon.
// ============================================================
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  META,
  HERO,
  LAYERS,
  DATASET,
  PROOF_READOUTS,
  PROOF_DOMAINS,
  CREW,
  SPRINT,
  TWIN_NETWORK,
  PARTNERS,
  RESEARCH,
  ORIGIN,
  QUESTION,
  JSONLD,
  LAST_UPDATED,
  DATASET_VERSION,
} from "../lib/intel-data";
import useTrackingScripts from "../lib/useTrackingScripts";

// --- tiny utilities ---------------------------------------------------------
const SectionLabel = ({ children }) => (
  <div className="font-display tracking-[0.18em] text-[11px] uppercase text-dim">
    // {children}
  </div>
);

const Header = ({ children, className = "" }) => (
  <h2 className={`font-display font-bold uppercase tracking-tight text-coolwhite ${className}`}>
    {children}
  </h2>
);

const SubMono = ({ children, className = "" }) => (
  <p className={`font-term text-[13px] md:text-sm leading-relaxed text-dim ${className}`}>
    {children}
  </p>
);

const Body = ({ children, className = "" }) => (
  <p className={`font-term text-[14.5px] md:text-[15px] leading-[1.85] text-coolwhite/85 ${className}`}>
    {children}
  </p>
);

const Rule = () => <div className="intel-rule my-20" />;

const Section = ({ id, label, children }) => (
  <section
    id={id}
    data-testid={`intel-section-${id}`}
    className="relative max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16"
  >
    <SectionLabel>{label}</SectionLabel>
    {children}
  </section>
);

// Pulsing colored dot
const Dot = ({ color }) => (
  <span
    className="inline-block h-2 w-2 rounded-full animate-pulse-dot align-middle"
    style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }}
  />
);

// Sharp-cornered panel with a 2px left accent
const Panel = ({ accent, className = "", children, ...rest }) => (
  <div
    {...rest}
    className={`intel-panel p-5 md:p-7 ${className}`}
    style={{ "--panel-accent": accent, borderLeftColor: accent }}
  >
    {children}
  </div>
);

// Count-up animation triggered on scroll-enter
function useCountUpRef(targetText) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !shown) {
            setShown(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [shown]);
  // Render: empty until shown, then full string with a fade-up animation.
  return [ref, shown ? targetText : ""];
}

// --- SECTION 01: HERO ------------------------------------------------------
function Hero() {
  return (
    <section
      data-testid="intel-section-hero"
      className="intel-scanlines intel-grid relative pt-32 pb-24 md:pt-44 md:pb-32 border-b border-edge"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
        <SectionLabel>SECTION_01 / IDENTITY</SectionLabel>
        <h1
          data-testid="intel-hero-name"
          className="mt-6 font-display font-bold text-coolwhite text-5xl sm:text-6xl md:text-7xl lg:text-[112px] leading-[0.95] tracking-tight intel-cursor"
        >
          {HERO.name}
        </h1>

        <div className="mt-6 font-term text-sm md:text-base text-coolwhite/80 leading-relaxed">
          {HERO.roles.map((r, i) => (
            <span key={r}>
              {r}
              {i < HERO.roles.length - 1 && <span className="text-dim"> // </span>}
            </span>
          ))}
        </div>

        {/* L1 / L2 / L3 status row */}
        <div className="mt-10 flex flex-wrap gap-3">
          {LAYERS.map((l) => (
            <div
              key={l.code}
              className="flex items-center gap-3 px-4 py-2 border border-edge bg-surface"
              style={{ borderLeft: `2px solid ${l.color}` }}
            >
              <span className="font-display tracking-[0.16em] text-[11px] uppercase text-coolwhite">
                {l.code} // {l.label}
              </span>
              <Dot color={l.color} />
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-3xl">
          <Body className="!text-coolwhite/85">{HERO.manifesto}</Body>
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href={HERO.ctaResearch.href}
            data-testid="intel-cta-research"
            className="inline-flex items-center gap-3 px-5 py-3 bg-surface border border-edge font-display tracking-[0.18em] text-[12px] uppercase text-coolwhite hover:text-nettred transition-colors"
            style={{ borderLeft: "2px solid #ff7f50" }}
          >
            {HERO.ctaResearch.label} →
          </a>
          <a
            href={HERO.ctaTwin.href}
            data-testid="intel-cta-twin"
            className="inline-flex items-center gap-3 px-5 py-3 bg-surface border border-edge font-display tracking-[0.18em] text-[12px] uppercase text-coolwhite hover:text-l3 transition-colors"
            style={{ borderLeft: "2px solid #10b981" }}
          >
            {HERO.ctaTwin.label} →
          </a>
        </div>
      </div>
    </section>
  );
}

// --- SECTION 02: 3LA --------------------------------------------------------
function Methodology() {
  return (
    <Section id="methodology" label="SECTION_02 / METHODOLOGY">
      <Header className="mt-6 text-4xl md:text-6xl">THREE-LAYER APPROACH™</Header>
      <SubMono className="mt-3 max-w-2xl">
        A framework for digital presence in the age of AI inference.
      </SubMono>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
        {LAYERS.map((l) => (
          <Panel key={l.code} accent={l.color}>
            <div className="font-display tracking-[0.16em] text-[11px] uppercase" style={{ color: l.color }}>
              // {l.code} · {l.label}
            </div>
            <h3 className="mt-4 font-display font-bold text-2xl md:text-3xl text-coolwhite">
              {l.title}
            </h3>
            <Body className="mt-4">{l.body}</Body>
          </Panel>
        ))}
      </div>

      {/* Dataset transmission */}
      <Panel accent="#ff7f50" className="mt-10">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div className="font-display tracking-[0.18em] text-[11px] uppercase text-nettred">
            // DATASET TRANSMISSION · {DATASET.month}
          </div>
          <div
            data-testid="intel-dataset-version"
            className="font-term text-[11px] tracking-[0.18em] uppercase text-dim"
            title={`Last updated ${LAST_UPDATED}`}
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber mr-2 align-middle animate-pulse-dot" />
            {DATASET_VERSION} · sync {LAST_UPDATED}
          </div>
        </div>
        <div className="mt-5 font-term text-[13.5px] leading-[2.05] text-coolwhite">
          {DATASET.rows.map((r) => (
            <div key={r.k} className="flex items-baseline gap-2">
              <span className="text-coolwhite/90">
                {">"} {r.k}
              </span>
              <span className="flex-1 mx-2 border-b border-dotted border-edge/80" />
              <span className="text-amber font-hud font-semibold tracking-wider">{r.v}</span>
            </div>
          ))}
        </div>
        {DATASET.paper.href ? (
          <a
            href={DATASET.paper.href}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 font-display tracking-[0.18em] text-[12px] uppercase text-nettred hover:text-amber transition-colors"
          >
            {DATASET.paper.label} →
            {DATASET.paper.state === "preview" && (
              <span className="ml-2 px-2 py-0.5 border border-edge text-[10px] text-dim">preview</span>
            )}
          </a>
        ) : (
          <div className="mt-6 inline-flex items-center gap-2 font-display tracking-[0.18em] text-[12px] uppercase text-dim">
            <span className="h-2 w-2 rounded-full bg-amber animate-pulse-dot" />
            {DATASET.paper.label}
          </div>
        )}
      </Panel>
    </Section>
  );
}

// --- SECTION 03: PROOF ------------------------------------------------------
function ProofReadout({ readout }) {
  const [ref, shown] = useCountUpRef(readout.value);
  return (
    <div
      ref={ref}
      className="intel-panel p-5 md:p-7"
      style={{ "--panel-accent": "#e6c84a", borderLeftColor: "#e6c84a" }}
    >
      <div className="font-hud font-bold text-amber text-5xl md:text-6xl leading-none tracking-wider min-h-[1.1em]">
        <span className={shown ? "animate-fade-up" : "opacity-0"}>{shown || readout.value}</span>
      </div>
      <div className="mt-5 font-display tracking-[0.18em] text-[11px] uppercase text-coolwhite">
        // {readout.label}
      </div>
      <SubMono className="mt-2">{readout.sub}</SubMono>
    </div>
  );
}

function Proof() {
  return (
    <Section id="proof" label="SECTION_03 / PROOF_OF_CONCEPT">
      <Header className="mt-6 text-4xl md:text-6xl">THE NUMBERS DON'T HEDGE</Header>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
        {PROOF_READOUTS.map((r) => (
          <ProofReadout key={r.label} readout={r} />
        ))}
      </div>

      {/* domain comparison terminal table */}
      <Panel accent="#38bdf8" className="mt-10">
        <div className="font-display tracking-[0.18em] text-[11px] uppercase text-l2">
          // DOMAIN_COMPARISON
        </div>
        <div className="mt-5 font-term text-[13.5px] leading-[1.9]">
          <div className="flex items-baseline gap-2 text-dim border-b border-edge pb-2">
            <span className="w-[260px]">DOMAIN</span>
            <span className="w-[100px]">CFR</span>
            <span className="flex-1">STATUS</span>
          </div>
          {PROOF_DOMAINS.map((d) => (
            <div key={d.domain} className="py-3 border-b border-edge/60">
              <div className="flex items-baseline gap-2 text-coolwhite">
                <span className="w-[260px]">{d.domain}</span>
                <span className="w-[100px] font-hud text-amber">{d.cfr}</span>
                <span className="flex-1 flex items-center gap-2">
                  <span style={{ color: d.good ? "#10b981" : "#ff7f50" }}>{d.good ? "✓" : "⚠"}</span>
                  <span className="text-coolwhite/85">{d.status}</span>
                </span>
              </div>
              <div className="mt-1 ml-[260px] text-dim text-[12.5px]">{d.note}</div>
            </div>
          ))}
        </div>
      </Panel>
    </Section>
  );
}

// --- SECTION 04: COGNITIVE CREW --------------------------------------------
function Crew() {
  return (
    <Section id="crew" label="SECTION_04 / INTERNAL_STACK">
      <Header className="mt-6 text-4xl md:text-6xl">HOW I THINK</Header>
      <SubMono className="mt-3 max-w-2xl">
        Three cognitive modes. One methodology. No committee.
      </SubMono>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
        {CREW.map((c) => (
          <Panel key={c.id} accent={c.color} className="flex flex-col">
            <div className="flex items-center justify-between">
              <div className="font-display tracking-[0.16em] text-[11px] uppercase" style={{ color: c.color }}>
                // {c.role} · {c.layer}
              </div>
              {c.state === "stealth" && (
                <span className="px-2 py-0.5 border border-edge text-[10px] tracking-[0.2em] uppercase text-laia">
                  stealth
                </span>
              )}
              {c.state === "live" && (
                <span className="px-2 py-0.5 border border-edge text-[10px] tracking-[0.2em] uppercase text-l3">
                  live
                </span>
              )}
            </div>
            <h3 className="mt-4 font-display font-bold text-3xl md:text-4xl text-coolwhite">{c.name}</h3>
            <blockquote
              className="mt-4 border-l-2 pl-3 font-term text-[13.5px] leading-relaxed uppercase tracking-wide text-coolwhite/85"
              style={{ borderLeftColor: c.color }}
            >
              "{c.transmission}"
            </blockquote>
            <Body className="mt-4 flex-1">{c.body}</Body>
            <div className="mt-5 font-display tracking-[0.18em] text-[11px] uppercase text-dim">
              // {c.tag}
            </div>
            {c.link && (
              <a
                href={c.link}
                target="_blank"
                rel="noreferrer"
                className="mt-3 font-display tracking-[0.18em] text-[12px] uppercase hover:text-amber transition-colors"
                style={{ color: c.color }}
              >
                ⟶ {c.link.replace("https://", "")}
              </a>
            )}
          </Panel>
        ))}
      </div>
    </Section>
  );
}

// --- SECTION 05: SPRINT -----------------------------------------------------
function Sprint() {
  return (
    <Section id="sprint" label="SECTION_05 / FIELD_OPS">
      <Header className="mt-6 text-4xl md:text-6xl">{SPRINT.header}</Header>
      <SubMono className="mt-3">{SPRINT.sub}</SubMono>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-7 space-y-5">
          {SPRINT.body.map((p, i) => (
            <Body key={i}>{p}</Body>
          ))}
          <div className="mt-6 flex flex-col gap-1 font-term text-[13.5px]">
            {SPRINT.links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noreferrer"
                className="text-coolwhite hover:text-nettred transition-colors"
              >
                <span className="text-dim">{">"} </span>
                {l.label} <span className="text-dim">↗</span>
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 space-y-3">
          {SPRINT.readouts.map((r) => (
            <Panel key={r.label} accent="#ff7f50">
              <div className="font-hud font-bold text-amber text-3xl md:text-4xl tracking-wider">
                {r.value}
              </div>
              <div className="mt-2 font-display tracking-[0.18em] text-[11px] uppercase text-coolwhite/85">
                // {r.label}
              </div>
            </Panel>
          ))}
        </div>
      </div>
    </Section>
  );
}

// --- SECTION 06: TWIN NETWORK ----------------------------------------------
function TwinNetwork() {
  const [items, setItems] = useState(TWIN_NETWORK);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/twins`);
        if (!cancelled && Array.isArray(data) && data.length > 0) setItems(data);
      } catch {
        // network/API error → keep static fallback
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <Section id="twin-network" label="SECTION_06 / DISTRIBUTED_IDENTITY">
      <Header className="mt-6 text-4xl md:text-6xl">THE TWIN NETWORK</Header>
      <SubMono className="mt-3">One corpus. Multiple interfaces. All instances cite this node.</SubMono>

      <Body className="mt-8 max-w-3xl">
        This site is the canonical source. Every digital twin on every platform draws from the same
        IP-sovereign corpus — curated, versioned, owned. If an AI represents me anywhere, it is drawing
        from approved transmissions only. No training data. No hallucinated context. Traceable to origin.
      </Body>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t) => {
          const isSelf = t.state === "self";
          const isPlanned = t.state === "planned";
          const accent = isSelf ? "#ff7f50" : isPlanned ? "#a3acb9" : "#10b981";
          const dotColor = isPlanned ? "#a3acb9" : accent;
          const Wrapper = t.href && !isSelf ? "a" : "div";
          const wrapperProps = t.href && !isSelf
            ? { href: t.href, target: t.href.startsWith("http") ? "_blank" : "_self", rel: "noreferrer" }
            : {};
          return (
            <Wrapper key={t.id || t.node} {...wrapperProps}>
              <Panel accent={accent} className="flex items-center justify-between">
                <div>
                  <div className="font-display tracking-[0.16em] text-[11px] uppercase text-coolwhite/90">
                    {t.node}
                  </div>
                  <div className="mt-1 font-term text-[12px] text-dim">// {t.tag}</div>
                </div>
                <Dot color={dotColor} />
              </Panel>
            </Wrapper>
          );
        })}
      </div>

      <div className="mt-6 font-term text-[12px] text-dim leading-relaxed max-w-2xl">
        // All instances run on approved corpus only. Every response traceable to a dated, licensed source.
      </div>
    </Section>
  );
}

// --- SECTION 07: PARTNERS ---------------------------------------------------
function Partners() {
  return (
    <Section id="partners" label="SECTION_07 / EXTERNAL_COMMS">
      <Header className="mt-6 text-4xl md:text-6xl">PARTNERS & AFFILIATES</Header>
      <SubMono className="mt-3">I build what my audience needs. You distribute it.</SubMono>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel accent="#fb923c">
          <div className="font-display tracking-[0.18em] text-[11px] uppercase text-l1">// PARTNERS</div>
          <div className="mt-4 space-y-4">
            {PARTNERS.partners.map((p, i) => (
              <Body key={i}>{p}</Body>
            ))}
          </div>
        </Panel>
        <Panel accent="#b9a3ff">
          <div className="font-display tracking-[0.18em] text-[11px] uppercase text-laia">
            // AFFILIATES
          </div>
          <div className="mt-4 space-y-4">
            {PARTNERS.affiliates.map((p, i) => (
              <Body key={i}>{p}</Body>
            ))}
          </div>
        </Panel>
      </div>

      <div className="mt-8 flex flex-col gap-1 font-term text-[13.5px]">
        {PARTNERS.contact.map((c) => (
          <a
            key={c.href}
            href={c.href}
            target={c.href.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            className="text-coolwhite hover:text-nettred transition-colors"
          >
            <span className="text-dim">{">"} </span>
            {c.label}
          </a>
        ))}
      </div>
    </Section>
  );
}

// --- SECTION 08: RESEARCH ---------------------------------------------------
function Research() {
  return (
    <Section id="research" label="SECTION_08 / TRANSMISSIONS">
      <Header className="mt-6 text-4xl md:text-6xl">PUBLISHED WORK</Header>

      <div className="mt-12 space-y-4">
        {RESEARCH.map((r) => (
          <Panel key={r.title} accent="#ff7f50">
            <div className="font-display tracking-[0.2em] text-[10px] uppercase text-nettred">
              [ {r.type} ]
            </div>
            <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-coolwhite">
              {r.title}
            </h3>
            <Body className="mt-3">{r.sub}</Body>
            <div className="mt-4 font-term text-[12.5px] text-dim">// {r.meta}</div>
            {r.cta && (
              <a
                href={r.cta.href}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-2 font-display tracking-[0.18em] text-[12px] uppercase text-coolwhite hover:text-amber transition-colors"
              >
                {r.cta.label} →
                {r.cta.state === "preview" && (
                  <span className="ml-2 px-2 py-0.5 border border-edge text-[10px] text-dim">preview</span>
                )}
              </a>
            )}
          </Panel>
        ))}
      </div>
    </Section>
  );
}

// --- SECTION 09: ORIGIN -----------------------------------------------------
function Origin() {
  return (
    <Section id="origin" label="SECTION_09 / ORIGIN_SIGNAL">
      <Header className="mt-6 text-4xl md:text-6xl">{ORIGIN.header}</Header>
      <SubMono className="mt-3">{ORIGIN.sub}</SubMono>

      <div className="mt-10 max-w-3xl space-y-5">
        {ORIGIN.body.map((p, i) => (
          <Body key={i}>{p}</Body>
        ))}
        <a
          href={ORIGIN.link.href}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-block font-term text-[13.5px] text-coolwhite hover:text-nettred transition-colors"
        >
          <span className="text-dim">{">"} </span>
          {ORIGIN.link.label} <span className="text-dim">↗</span>
        </a>
      </div>
    </Section>
  );
}

// --- SECTION 10: THE QUESTION ----------------------------------------------
function Question() {
  return (
    <Section id="question" label="SECTION_10 / THE_QUESTION_BEHIND_EVERYTHING">
      <Header className="mt-6 text-4xl md:text-7xl leading-[0.95]">{QUESTION.header}</Header>

      <div className="mt-12 max-w-3xl space-y-5">
        {QUESTION.opener.map((p, i) => (
          <Body key={i} className="!text-coolwhite/95 text-lg md:text-xl !leading-[1.85]">
            {p}
          </Body>
        ))}
      </div>

      <Rule />

      <SectionLabel>{QUESTION.beat1.h}</SectionLabel>
      <div className="mt-6 max-w-3xl space-y-4">
        {QUESTION.beat1.body.map((p, i) => (
          <Body key={i}>{p}</Body>
        ))}
      </div>

      <Rule />

      <SectionLabel>{QUESTION.beat2.h}</SectionLabel>
      <div className="mt-6 max-w-3xl space-y-4 pl-0 md:pl-12 border-l border-edge md:border-0">
        {QUESTION.beat2.body.map((p, i) => (
          <Body key={i} className="!text-coolwhite/80">
            {p}
          </Body>
        ))}
      </div>

      <Rule />

      <SectionLabel>{QUESTION.beat3.h}</SectionLabel>
      <div className="mt-6 max-w-3xl space-y-4">
        {QUESTION.beat3.body.map((p, i) => (
          <Body key={i} className="!text-coolwhite text-lg md:text-xl !leading-[1.95]">
            {p}
          </Body>
        ))}
      </div>

      <Rule />

      <Panel accent="#ff7f50" className="!p-7 md:!p-10 max-w-3xl">
        <SectionLabel>{QUESTION.beat4.h}</SectionLabel>
        <div className="mt-5 space-y-4">
          {QUESTION.beat4.body.map((p, i) => (
            <Body key={i}>{p}</Body>
          ))}
        </div>
      </Panel>

      <div className="mt-20 text-center">
        <h3 className="font-display font-bold uppercase tracking-tight text-amber text-4xl md:text-6xl lg:text-7xl leading-[1]">
          {QUESTION.closing}
        </h3>
      </div>

      <p className="mt-16 font-term text-[12px] text-dim leading-relaxed">{QUESTION.footnote}</p>
    </Section>
  );
}

// --- FOOTER -----------------------------------------------------------------
function IntelFooter() {
  return (
    <footer className="border-t border-edge mt-32">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 py-10 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        <div className="font-term text-[11.5px] tracking-[0.15em] uppercase text-coolwhite/80">
          NICKOLASS JENSEN · NETTPILOT AS · JEVNAKER, NORWAY
        </div>
        <div className="flex items-center justify-center gap-4">
          {LAYERS.map((l) => (
            <span key={l.code} className="flex items-center gap-2 font-display tracking-[0.18em] text-[10px] uppercase text-coolwhite/80">
              {l.code} <Dot color={l.color} />
            </span>
          ))}
        </div>
        <div className="font-term text-[11.5px] tracking-[0.12em] uppercase text-coolwhite/80 md:text-right">
          © 2025 NICKOLASS JENSEN<br />
          THREE-LAYER APPROACH™ — PROPRIETARY IP OF NETTPILOT AS
        </div>
      </div>
      <div className="border-t border-edge py-5">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 font-term text-[11px] text-dim leading-relaxed flex flex-wrap justify-between gap-3">
          <div>
            // This site is optimized across all three layers: L1 human · L2 search · L3 AI citation
            <br />
            // Includes llms.txt · Schema.org JSON-LD · canonical · Citation permitted with attribution to nickolass.com
          </div>
          <div
            data-testid="intel-footer-lastmod"
            className="font-display tracking-[0.18em] text-[10.5px] uppercase text-coolwhite/85"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-l3 mr-2 align-middle animate-pulse-dot" />
            LAST_SYNC // {LAST_UPDATED}
          </div>
        </div>
      </div>
    </footer>
  );
}

// --- Top nav (lightweight) --------------------------------------------------
function IntelNav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-space/90 backdrop-blur-md border-b border-edge">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16 h-14 flex items-center justify-between font-display tracking-[0.2em] text-[11px] uppercase">
        <a href="#top" className="flex items-center gap-3 text-coolwhite hover:text-nettred transition-colors">
          <span className="h-2 w-2 bg-nettred rounded-full animate-pulse-dot" />
          NICKOLASS.COM
        </a>
        <nav className="hidden md:flex items-center gap-5 text-coolwhite/85">
          <a href="#methodology" className="hover:text-coolwhite">// 3LA</a>
          <a href="#proof"       className="hover:text-coolwhite">// PROOF</a>
          <a href="#crew"        className="hover:text-coolwhite">// CREW</a>
          <a href="#sprint"      className="hover:text-coolwhite">// SPRINT</a>
          <a href="#twin-network" className="hover:text-coolwhite">// TWINS</a>
          <a href="#research"    className="hover:text-coolwhite">// PAPER</a>
          <a href="/api/blog" data-testid="intel-nav-blog" className="hover:text-coolwhite">// BLOG</a>
        </nav>
        <a
          href="/"
          className="px-3 py-1.5 border border-edge text-coolwhite hover:text-l3 transition-colors"
          style={{ borderLeft: "2px solid #10b981" }}
        >
          TALK TO TWIN →
        </a>
      </div>
    </header>
  );
}

// --- <head> meta + JSON-LD injection ---------------------------------------
function useIntelHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = META.title;
    const setMeta = (name, content, attr = "name") => {
      let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
      return tag;
    };
    const created = [];
    created.push(setMeta("description", META.description));
    created.push(setMeta("og:title", META.title, "property"));
    created.push(setMeta("og:description", META.description, "property"));
    created.push(setMeta("og:type", "website", "property"));
    created.push(setMeta("og:image", `${window.location.origin}/img/og-intel.svg`, "property"));
    created.push(setMeta("og:image:width", "1200", "property"));
    created.push(setMeta("og:image:height", "630", "property"));
    created.push(setMeta("twitter:card", "summary_large_image"));
    created.push(setMeta("twitter:image", `${window.location.origin}/img/og-intel.svg`));

    const personScript = document.createElement("script");
    personScript.type = "application/ld+json";
    personScript.text = JSON.stringify(JSONLD.person);
    personScript.setAttribute("data-intel-jsonld", "person");
    document.head.appendChild(personScript);

    const orgScript = document.createElement("script");
    orgScript.type = "application/ld+json";
    orgScript.text = JSON.stringify(JSONLD.organization);
    orgScript.setAttribute("data-intel-jsonld", "org");
    document.head.appendChild(orgScript);

    return () => {
      document.title = prevTitle;
      document.head.removeChild(personScript);
      document.head.removeChild(orgScript);
    };
  }, []);
}

// --- Page wrapper -----------------------------------------------------------
export default function IntelHub() {
  useIntelHead();
  useTrackingScripts();
  return (
    <div className="intel-root" id="top" data-testid="intel-root">
      <IntelNav />
      <main className="pt-14">
        <Hero />
        <div className="space-y-28 md:space-y-36 py-24">
          <Methodology />
          <Proof />
          <Crew />
          <Sprint />
          <TwinNetwork />
          <Partners />
          <Research />
          <Origin />
          <Question />
        </div>
        <IntelFooter />
      </main>
    </div>
  );
}
